import { Edit } from '@mui/icons-material';
import { Button, Grid, IconButton, Skeleton, type Theme, useMediaQuery } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import Header from '/@/components/bayer/header';
import PageContainer from '/@/components/bayer/page-container';
import CropProtectionRecommendationDetailCard from '/@/components/crop-protection/output/recommendation';
import RecommendationInputsCard from '/@/components/crop-protection/output/recommendation-inputs-card';
import RecommendationCommentsButton from '/@/components/recommendation-comments-button';
import RecommendationCommentsDrawer from '/@/components/recommendation-comments-drawer';
import RecommendationShare from '/@/components/recommendation-share';
import { useBreadcrumbs } from '/@/hooks/breadcrumbs';
import {
    useProcessRecommendation,
    useRecommendationById,
} from '/@/hooks/my-recommendations/useFilterMyRecommendations';
import { useRecommendationCommentsPermissions } from '/@/hooks/recommendation';
import { _ } from '/@/locale';
import { formatDate } from '/@/utils';
import { reverse } from '/@/utils/router';

const CropProtectionRecommendationDetail = ({ recommendationId }: { recommendationId: string }) => {
    const navigate = useNavigate();
    const recoQuery = useRecommendationById(Number(recommendationId));
    const breadcrumbs = useBreadcrumbs('cropProtectionRecommendation.CropProtectionRecommendationDetail');
    const [commentsDrawerOpen, setCommentsDrawerOpen] = useState(false);
    const { shouldShowCommentsButton } = useRecommendationCommentsPermissions(
        recoQuery.data?.commentsStatus,
        recoQuery.data?.userNode
    );

    const title = recoQuery.data?.weedV2
        ? _('Recomendación de herbicidas')
        : recoQuery.data?.diseaseRecommendation
          ? _('Recomendación de fungicidas')
          : recoQuery.data?.seedTreatmentRecommendation
            ? _('Recomendación de tratamiento de semillas')
            : _('Recomendación de insecticidas');

    const processRecommendation = useProcessRecommendation();
    const processedRecommendation = recoQuery.data ? processRecommendation(recoQuery.data) : undefined;
    const isSmOrLower = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    return (
        <PageContainer
            heroHeader={
                <Header
                    variant='full-gray'
                    title={title}
                    subtitle={`${_(
                        'Nuestras recomendaciones se basan en tus respuestas y en los estudios y tendencias regionales de nuestra base de datos.'
                    )} ${
                        recoQuery.data?.updated
                            ? _('Última actualización {{date}} {{time}}', {
                                  date: formatDate(new Date(recoQuery.data?.updated)),
                                  time: new Date(recoQuery.data?.updated).toLocaleTimeString(),
                              })
                            : ''
                    }`}
                    actions={
                        recoQuery.data?.status === 'draft'
                            ? [
                                  <IconButton
                                      key='edit-recommendation'
                                      color='secondary'
                                      size='small'
                                      sx={{
                                          display: {
                                              xs: 'inherith',
                                              sm: 'none',
                                          },
                                      }}
                                      onClick={() => {
                                          return navigate(
                                              reverse('cropProtectionRecommendation', {
                                                  params: {
                                                      recommendationId: recoQuery.data?.id,
                                                  },
                                                  qs: {
                                                      fieldId: recoQuery?.data.field,
                                                      userNodeId: recoQuery?.data.userNode,
                                                  },
                                              })
                                          );
                                      }}
                                  >
                                      <Edit />
                                  </IconButton>,
                              ]
                            : [
                                  <RecommendationShare
                                      key='recommendation-share'
                                      type={'crop-protection'}
                                      recommendation={{
                                          ...processedRecommendation,
                                          ...recoQuery.data?.backup,
                                          ...recoQuery.data,
                                      }}
                                      disabled={!recoQuery.data}
                                      version={2}
                                  />,
                                  recoQuery.data?.id &&
                                      shouldShowCommentsButton &&
                                      (isSmOrLower ? (
                                          <Button
                                              variant='contained'
                                              color='primary'
                                              fullWidth
                                              onClick={() => setCommentsDrawerOpen(true)}
                                          >
                                              {_('Ver comentarios')}
                                          </Button>
                                      ) : (
                                          <RecommendationCommentsButton
                                              key='comments-button'
                                              commentsStatus={recoQuery.data.commentsStatus}
                                              onOpenDrawer={() => setCommentsDrawerOpen(true)}
                                          />
                                      )),
                              ].filter(Boolean)
                    }
                    breadcrumbs={breadcrumbs}
                />
            }
        >
            <Grid container spacing={2}>
                <Grid
                    item
                    xs={12}
                    sm={12}
                    md={3}
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                    }}
                >
                    {recoQuery.isValidating ? (
                        <Skeleton variant='rounded' height={400} />
                    ) : (
                        <RecommendationInputsCard
                            isPDF={false}
                            sx={{
                                p: 2,
                            }}
                            recommendationId={Number(recommendationId)}
                        />
                    )}
                </Grid>
                <Grid item xs={12} sm={12} md={9}>
                    <CropProtectionRecommendationDetailCard />
                </Grid>
            </Grid>
            {recoQuery.data?.id && (
                <RecommendationCommentsDrawer
                    open={commentsDrawerOpen}
                    setCommentsDrawerOpen={setCommentsDrawerOpen}
                    recommendationId={recoQuery.data.id}
                />
            )}
        </PageContainer>
    );
};

export default CropProtectionRecommendationDetail;
